import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  RECOVERY_STATE_FILTER_OPTIONS,
  recoveryStateLabel,
} from "../../lib/emailHygieneRecoveryState";
import { ArrowLeft } from "lucide-react";
import EmailHygieneReconciliationQueueTable from "./EmailHygieneReconciliationQueueTable";
import EmailHygieneReconciliationDetail from "./EmailHygieneReconciliationDetail";
import EmailHygieneReconciliationConfirmDialog from "./EmailHygieneReconciliationConfirmDialog";
import { useEmailHygieneReconciliationQueue } from "../../hooks/useEmailHygieneReconciliationQueue";
import { useEmailHygieneReconciliationDetail } from "../../hooks/useEmailHygieneReconciliationDetail";
import { useModuleAccessLevel } from "../../hooks/usePermissions";
import {
  executeEmailHygieneReconciliationAction,
  extractApiErrorMessage,
} from "../../lib/emailHygieneReconciliationApi";
import { toast } from "../../hooks/use-toast";

// EMAIL-HYGIENE-RECOVERY-STATE-001 (Phase 1): labels come from the single
// shared presentation module so the queue table, the detail view and this
// filter can never drift apart. Values are the backend enum verbatim.
const RECOVERY_STATE_OPTIONS = [
  { value: "all", label: "All" },
  ...RECOVERY_STATE_FILTER_OPTIONS.map((value) => ({
    value,
    label: recoveryStateLabel(value) || value,
  })),
];

const REASON_OPTIONS = [
  { value: "all", label: "All Reasons" },
  { value: "contact_missing", label: "Contact Missing" },
  { value: "lookup_failed", label: "Lookup Failed" },
  { value: "projection_failed", label: "Projection Failed" },
  { value: "dnd_failed", label: "DND Failed" },
  { value: "configuration_error", label: "Configuration Error" },
  { value: "state_conflict", label: "State Conflict" },
  { value: "recipient_identity_invalid", label: "Recipient Identity Invalid" },
];

const DEFAULT_FILTERS = { environment: "all", reason: "all", recipientEmail: "", recoveryState: "all" };
const PAGE_SIZE = 25;

/** Interprets the real, structured RECON-003 execute response — never
 * collapsed into a bare "Success" banner (this checkpoint's own explicit
 * requirement). */
function describeExecuteResult(result) {
  // EMAIL-HYGIENE-RECOVERY-EVIDENCE-001 (Gate D): `verify_provider_state`
  // returns ExecuteProviderVerificationResponse, which reports an
  // OBSERVATION -- it carries no `resolved`/`fully_finalized` at all, so it
  // previously fell through to the "re-attempted but remains unresolved"
  // branch below. That told an operator the opposite of the truth: nothing
  // was re-attempted, no mutation occurred, and the reconciliation staying
  // open is the designed outcome rather than a failure. Keyed on
  // `verification_outcome`, the field only this response shape carries.
  if (result.verification_outcome) {
    return {
      title: "Provider State Verified",
      description: "Provider state verified. The reconciliation remains open.",
    };
  }
  if (result.resolved && result.fully_finalized) {
    return { title: "Resolved", description: "Business reconciliation and secondary bookkeeping both completed." };
  }
  if (result.resolved && !result.fully_finalized) {
    return {
      title: "Resolved — Administrative Finalization Required",
      description: "The business mutation succeeded. Some bookkeeping steps did not complete — see the detail view.",
    };
  }
  return {
    title: "Still Unresolved",
    description: "The reconciliation was re-attempted but remains unresolved and stays in the active queue.",
  };
}

/**
 * EmailHygieneReconciliationView — DASH-EMAIL-013.
 *
 * The Email Hygiene Reconciliation drill-in surface, reached only from the
 * Deliverability & Hygiene "Reconciliation Required" tile — deliberately
 * NOT a new top-level Email Dashboard view/tab (see EmailDashboardPanel.jsx
 * / EmailDeliverabilityView.jsx). Owns queue filters/pagination, the
 * queue<->detail drill-down (mirroring EmailCreativeView's own
 * selectedCreativeId pattern), and governed-action execution orchestration.
 *
 * RBAC: read access is already gated by whatever guard renders the Email
 * Dashboard tab itself (war_room/full, matching the backend's own read
 * guard). Execute capability is separately gated here via
 * `useModuleAccessLevel("admin").hasFullAccess` — matching the backend's
 * own `admin`/`full` execute guard — so a read-only operator sees the
 * queue/detail but never a mutation control. This is UI-only convenience;
 * the backend remains the authoritative enforcement (a stale/incorrect
 * frontend permission read fails closed into a handled 403, not a granted
 * capability — see handleConfirm below).
 */
export default function EmailHygieneReconciliationView({ onBack }) {
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [offset, setOffset] = useState(0);
  const [selectedReconciliationId, setSelectedReconciliationId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { hasFullAccess: canExecute } = useModuleAccessLevel("admin");

  const queue = useEmailHygieneReconciliationQueue({
    environment: appliedFilters.environment,
    reason: appliedFilters.reason,
    recipientEmail: appliedFilters.recipientEmail,
    recoveryState: appliedFilters.recoveryState,
    limit: PAGE_SIZE,
    offset,
  });
  const detail = useEmailHygieneReconciliationDetail(selectedReconciliationId);

  const applyFilters = () => {
    setOffset(0);
    setAppliedFilters(draftFilters);
  };
  const resetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setOffset(0);
    setAppliedFilters(DEFAULT_FILTERS);
  };
  const filtersDirty = JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters);

  const handleSelect = (reconciliationId) => {
    setSelectedReconciliationId(reconciliationId);
  };
  const handleBackFromDetail = () => {
    setSelectedReconciliationId(null);
    queue.refresh();
  };

  const handleSelectAction = (action) => {
    setSubmitError(null);
    setConfirmAction(action);
  };

  const handleConfirm = async (resolutionReason) => {
    if (!confirmAction || !selectedReconciliationId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await executeEmailHygieneReconciliationAction(
        selectedReconciliationId,
        confirmAction.action_type,
        { resolutionReason }
      );
      setConfirmAction(null);
      const { title, description } = describeExecuteResult(result);
      toast({ title, description });
      await detail.refresh();
      await queue.refresh();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setSubmitError("This reconciliation changed since it was loaded. Refreshing with the latest state.");
        await detail.refresh();
        await queue.refresh();
      } else if (status === 403) {
        setSubmitError("You do not have permission to execute this action.");
      } else {
        setSubmitError(extractApiErrorMessage(err, "Failed to execute this reconciliation action"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  // RECONCILIATION-DEBT-012A: page over what the API can actually return.
  // `queue.total` is the true population and may exceed the bounded discovery
  // window; paging over it would offer pages that come back empty.
  const browsableTotal = queue.reachableTotal || 0;
  const totalPages = Math.max(1, Math.ceil(browsableTotal / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="email-hygiene-reconciliation-view">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1.5 text-slate-300 hover:text-slate-100"
        onClick={onBack}
        data-testid="email-hygiene-reconciliation-view-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Deliverability &amp; Hygiene
      </Button>

      {selectedReconciliationId ? (
        <>
          {detail.loading && (
            <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-detail-loading">
              <CardContent className="p-8 text-center text-slate-400">Loading reconciliation…</CardContent>
            </Card>
          )}
          {!detail.loading && detail.error && (
            <Card className="glass border-red-500/20" data-testid="email-hygiene-reconciliation-detail-error">
              <CardContent className="p-4 text-sm text-red-300">{detail.error}</CardContent>
            </Card>
          )}
          {!detail.loading && !detail.error && detail.notFound && (
            <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-detail-not-found">
              <CardContent className="p-8 text-center text-slate-400">
                This reconciliation is no longer available. It may have already been resolved.
                <div className="mt-3">
                  <Button type="button" variant="outline" size="sm" onClick={handleBackFromDetail}>
                    Back to Queue
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {!detail.loading && !detail.error && detail.detail && (
            <>
              <EmailHygieneReconciliationDetail
                detail={detail.detail}
                canExecute={canExecute}
                onBack={handleBackFromDetail}
                onSelectAction={handleSelectAction}
              />
              <EmailHygieneReconciliationConfirmDialog
                open={!!confirmAction}
                onOpenChange={(open) => !open && setConfirmAction(null)}
                detail={detail.detail}
                action={confirmAction}
                submitting={submitting}
                error={submitError}
                onConfirm={handleConfirm}
              />
            </>
          )}
        </>
      ) : (
        <>
          <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-filters">
            <CardContent className="flex flex-wrap items-end gap-3 p-4">
              <div className="flex min-w-[160px] flex-col gap-1.5">
                <span className="text-xs text-slate-400">Environment</span>
                <Select
                  value={draftFilters.environment}
                  onValueChange={(v) => setDraftFilters((f) => ({ ...f, environment: v }))}
                >
                  <SelectTrigger className="bg-white/5 text-slate-100 border-white/10" data-testid="email-hygiene-reconciliation-filter-environment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="qualification">Qualification</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex min-w-[200px] flex-col gap-1.5">
                <span className="text-xs text-slate-400">Reason</span>
                <Select value={draftFilters.reason} onValueChange={(v) => setDraftFilters((f) => ({ ...f, reason: v }))}>
                  <SelectTrigger className="bg-white/5 text-slate-100 border-white/10" data-testid="email-hygiene-reconciliation-filter-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REASON_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex min-w-[220px] flex-col gap-1.5">
                <span className="text-xs text-slate-400">Recovery Status</span>
                <Select
                  value={draftFilters.recoveryState}
                  onValueChange={(v) => setDraftFilters((f) => ({ ...f, recoveryState: v }))}
                >
                  <SelectTrigger
                    className="bg-white/5 text-slate-100 border-white/10"
                    data-testid="email-hygiene-reconciliation-filter-recovery-state"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECOVERY_STATE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
                <span className="text-xs text-slate-400">Recipient Email (exact match)</span>
                <Input
                  value={draftFilters.recipientEmail}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, recipientEmail: e.target.value }))}
                  placeholder="recipient@example.com"
                  data-testid="email-hygiene-reconciliation-filter-recipient"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" disabled={!filtersDirty} onClick={applyFilters} data-testid="email-hygiene-reconciliation-filter-apply">
                  Search
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={resetFilters} data-testid="email-hygiene-reconciliation-filter-reset">
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {queue.loading && (
            <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-queue-loading">
              <CardContent className="p-8 text-center text-slate-400">Loading active reconciliations…</CardContent>
            </Card>
          )}
          {!queue.loading && queue.error && (
            <Card className="glass border-red-500/20" data-testid="email-hygiene-reconciliation-queue-error">
              <CardContent className="p-4 text-sm text-red-300">{queue.error}</CardContent>
            </Card>
          )}
          {!queue.loading && !queue.error && (
            <>
              <EmailHygieneReconciliationQueueTable items={queue.items} onSelect={handleSelect} />
              {queue.discoveryTruncated && (
                <p
                  className="text-xs text-amber-300/90 mb-2"
                  data-testid="email-hygiene-reconciliation-discovery-truncated"
                >
                  Showing the oldest {browsableTotal.toLocaleString()} of{" "}
                  {queue.total.toLocaleString()} records. Discovery is bounded at{" "}
                  {queue.discoveryCap.toLocaleString()} per class — narrow the filters to
                  reach the rest.
                </p>
              )}
              {browsableTotal > 0 && (
                <div className="flex items-center justify-between text-sm text-slate-400" data-testid="email-hygiene-reconciliation-pagination">
                  <span>
                    Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, browsableTotal)} of {browsableTotal.toLocaleString()}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      data-testid="email-hygiene-reconciliation-pagination-prev"
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={page >= totalPages}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                      data-testid="email-hygiene-reconciliation-pagination-next"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
